<template>
  <div v-if="show" class="app-modal" role="presentation">
    <div class="app-modal__backdrop" @click="emitClose"></div>

    <section
      class="app-modal__dialog payment-plan-pay-modal__dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="payment-plan-pay-modal-title"
      @click.stop
    >
      <header class="app-modal__header">
        <div>
          <h3 id="payment-plan-pay-modal-title">{{ text.payModal.title }}</h3>
          <p v-if="modalSubtitle !== ''">{{ modalSubtitle }}</p>
        </div>
        <button
          class="app-modal__close"
          type="button"
          :aria-label="text.payModal.closeAriaLabel"
          @click="emitClose"
        >
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      </header>

      <div class="app-modal__content payment-plan-pay-modal__content">
        <form class="app-modal__form payment-plan-pay-modal__form" @submit.prevent="emitSubmit">
          <PayModalSectionPlan
            v-if="!isExpenseFeeContext"
            :text="text"
            :is-cfdi-mode="isCfdiMode"
            :plan-customer="planCustomer"
            :plan-voucher-type-label="planVoucherTypeLabel"
            :plan-date="planDate"
            :plan-invoice-payment-method="planInvoicePaymentMethod"
            :format-date-short-month="formatDateShortMonth"
          />

          <PayModalSectionExpenseFeeInfo
            v-else
            :text="text"
            :counterparty-label="expenseFeeCounterpartyLabel"
            :counterparty-name="expenseFeeCounterpartyName"
            :voucher-type-label="expenseFeeVoucherTypeLabel"
            :scheduled-date="expenseFeeScheduledDate"
            :format-date-short-month="formatDateShortMonth"
          />

          <PayModalSectionBillingData
            v-if="!isExpenseFeeContext && isCfdiMode"
            :text="text"
            :billing-data-text="billingDataText"
            :receiver-options="receiverOptions"
            :selected-receiver="selectedReceiver"
            :customer-search-label="customerSearchLabel"
            @update:selectedReceiver="selectedReceiver = $event"
            @open-contributor="openContributorModal"
          />

          <PayModalSectionAmounts
            :text="text"
            :currency-code="currencyCode"
            :exchange-rate="exchangeRate"
            :amount="form.amount"
            :payment-date-model="paymentDateModel"
            :plan-amount="planAmount"
            :reference-amount-label="referenceAmountLabel"
            :remaining-amount="remainingAmount"
            :amount-error-message="amountErrorMessage"
            :amount-warning-message="amountWarningMessage"
            @update:currencyCode="currencyCode = $event"
            @update:exchangeRate="exchangeRate = $event"
            @update:amount="form.amount = $event"
            @update:paymentDateModel="paymentDateModel = $event"
          />

          <PayModalSectionPaymentInfo
            :text="text"
            :payment-method-options="paymentMethodOptions"
            :payment-account-options="paymentAccountOptions"
            :payment-method="form.paymentMethod"
            :operation-number="form.operationNumber"
            :payment-account="form.paymentAccount"
            :account-only="isExpenseFeeContext"
            @update:paymentMethod="form.paymentMethod = $event"
            @update:operationNumber="form.operationNumber = $event"
            @update:paymentAccount="form.paymentAccount = $event"
          />

          <PayModalSectionBanks
            v-if="!isExpenseFeeContext && isCfdiMode"
            :text="text"
            :expanded="bankDetailsExpanded"
            :beneficiary-bank-rfc="form.beneficiaryBankRfc"
            :beneficiary-account-number="form.beneficiaryAccountNumber"
            :ordering-bank="form.orderingBank"
            :ordering-bank-rfc="form.orderingBankRfc"
            :ordering-bank-account="form.orderingBankAccount"
            @update:expanded="bankDetailsExpanded = $event"
            @update:beneficiaryBankRfc="form.beneficiaryBankRfc = $event"
            @update:beneficiaryAccountNumber="form.beneficiaryAccountNumber = $event"
            @update:orderingBank="form.orderingBank = $event"
            @update:orderingBankRfc="form.orderingBankRfc = $event"
            @update:orderingBankAccount="form.orderingBankAccount = $event"
          />

          <footer class="app-modal__footer">
            <div class="app-modal__footer-right">
              <button type="button" class="btn btn-light" :disabled="submitting" @click="emitClose">
                {{ text.payModal.buttons.cancel }}
              </button>
              <button type="submit" class="btn btn-primary" :disabled="submitDisabled">
                {{ submitting ? text.payModal.buttons.submitting : text.payModal.buttons.submit }}
              </button>
              <button
                v-if="!isExpenseFeeContext && isCfdiMode"
                type="button"
                class="btn btn-success payment-plan-pay-modal__submit-cfdi-btn"
                :disabled="submitDisabled"
                @click="emitSubmitCfdi"
              >
                {{ submitting ? text.payModal.buttons.submitting : text.payModal.buttons.submitCfdi }}
              </button>
            </div>
          </footer>
        </form>
      </div>
    </section>

    <ContributorModal
      v-if="isCfdiMode"
      :show="showContributorModal"
      :mode="contributorModalMode"
      :initial-data="contributorModalInitialData"
      :state-options="[]"
      :regimen-options="effectiveTaxRegimeOptions"
      :uso-cfdi-options="effectiveCfdiUseOptions"
      :payment-method-options="effectivePaymentFormOptions"
      :payment-method-type-options="effectivePaymentMethodOptions"
      :loading="contributorModalLoading"
      @close="closeContributorModal"
      @submit="onContributorModalSubmit"
    />
  </div>
</template>

<script>
import generalCatalogsService from '@/services/generalCatalogs';
import financialAccountRepository from '@/services/financialAccountRepository';
import invoicesService from '@/services/invoices';
import customersService from '@/services/customers';
import ContributorModal from '@/components/contributors/ContributorModal.vue';
import PayModalSectionPlan from '@/components/pay/modal/sections/PayModalSectionPlan.vue';
import PayModalSectionExpenseFeeInfo from '@/components/pay/modal/sections/PayModalSectionExpenseFeeInfo.vue';
import PayModalSectionBillingData from '@/components/pay/modal/sections/PayModalSectionBillingData.vue';
import PayModalSectionAmounts from '@/components/pay/modal/sections/PayModalSectionAmounts.vue';
import PayModalSectionPaymentInfo from '@/components/pay/modal/sections/PayModalSectionPaymentInfo.vue';
import PayModalSectionBanks from '@/components/pay/modal/sections/PayModalSectionBanks.vue';
import { extractCollection, extractPayload } from '@/utils/dataNormalization';
import { normalizeCatalogOptions } from '@/utils/catalogOptions';
import { parsePresentationDate, formatDateForApi } from '@/utils/datePresentation';
import { useDomainText } from '@/composables/text/useDomainText';
import {
  resolvePayPlanAmount,
  resolveExpenseFeePayAmount,
  resolvePayPlanDate,
  resolvePayPlanCustomer,
  resolvePayPlanVoucherTypeLabel,
  resolvePayModalSimpleNoteMode,
  resolveExpenseFeeCounterpartyLabel,
  resolveExpenseFeeCounterpartyName,
  resolveExpenseFeeVoucherTypeLabel,
  resolveExpenseFeeEntityType,
  resolveExpenseFeeAmountErrorMessage,
  resolveExpenseFeeAmountWarningMessage,
  resolveRemainingAmount,
  resolveAmountErrorMessage,
  resolveAmountWarningMessage,
  resolveDefaultTransferPaymentMethod,
  buildPaymentAccountOptions,
  resolveDefaultPaymentAccount,
  formatPayPlanDateShortMonth,
  resolvePayModalCustomerSearchLabel,
  mapPayModalReceiverSelectorOption,
  mapPayModalContributorDetailOption,
  extractPayModalBankData,
  resolveReceiverOptionByRfc,
  resolveReceiverOptionByContributorId,
  resolveDefaultReceiverOption,
} from '@/composables/pay/usePayModalShared';

const { catalog: text } = useDomainText('payments');
const { catalog: billingDataText } = useDomainText('billingData');

export default {
  name: 'PayModal',
  components: {
    ContributorModal,
    PayModalSectionPlan,
    PayModalSectionExpenseFeeInfo,
    PayModalSectionBillingData,
    PayModalSectionAmounts,
    PayModalSectionPaymentInfo,
    PayModalSectionBanks,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    row: {
      type: Object,
      default: null,
    },
    context: {
      type: String,
      default: 'payment-plan',
    },
    entityType: {
      type: String,
      default: '',
    },
    submitting: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'submit'],
  data() {
    return {
      text,
      billingDataText,
      loadingCatalogs: false,
      loadingReceivers: false,
      paymentMethodOptions: [],
      paymentAccountOptions: [],
      taxRegimeOptions: [],
      cfdiUseOptions: [],
      paymentMethodTypeOptions: [],
      receiverOptions: [],
      selectedReceiver: null,
      showContributorModal: false,
      contributorModalLoading: false,
      contributorModalMode: 'edit',
      contributorModalInitialData: null,
      bankDetailsExpanded: false,
      form: {
        amount: 0,
        paymentDate: formatDateForApi(new Date()),
        paymentMethod: null,
        paymentAccount: null,
        operationNumber: '',
        beneficiaryBankRfc: '',
        beneficiaryAccountNumber: '',
        orderingBank: '',
        orderingBankRfc: '',
        orderingBankAccount: '',
      },
      currencyCode: 'MXN',
      exchangeRate: 1,
    };
  },
  computed: {
    isExpenseFeeContext() {
      return String(this.context || '').trim().toLowerCase() === 'expense-fee';
    },
    modalSubtitle() {
      if (this.isExpenseFeeContext) {
        return '';
      }

      return String(this.text?.payModal?.subtitle || '').trim();
    },
    isSimpleNoteMode() {
      return resolvePayModalSimpleNoteMode({
        context: this.context,
        row: this.row,
      });
    },
    isCfdiMode() {
      return !this.isSimpleNoteMode;
    },
    expenseFeeCounterpartyLabel() {
      return resolveExpenseFeeCounterpartyLabel(this.row, this.text);
    },
    expenseFeeCounterpartyName() {
      return resolveExpenseFeeCounterpartyName(this.row, this.text.payModal.fallback.emptyValue);
    },
    expenseFeeVoucherTypeLabel() {
      return resolveExpenseFeeVoucherTypeLabel(this.row, this.text);
    },
    expenseFeeEntityType() {
      return resolveExpenseFeeEntityType(this.entityType);
    },
    expenseFeeScheduledDate() {
      return String(this.row?.payment_date || this.row?.date || '').trim();
    },
    invoiceCustomerId() {
      return Number(this.row?.customer_id || this.row?.company_id || 0) || 0;
    },
    planAmount() {
      if (this.isExpenseFeeContext) {
        return resolveExpenseFeePayAmount(this.row);
      }

      return resolvePayPlanAmount(this.row);
    },
    referenceAmountLabel() {
      const summaryText = this.text?.payModal?.amountBox?.summary || {};

      if (!this.isExpenseFeeContext) {
        return String(summaryText.planAmount || '').trim();
      }

      if (this.expenseFeeEntityType === 'fee') {
        return String(summaryText.feeAmount || summaryText.planAmount || '').trim();
      }

      return String(summaryText.expenseAmount || summaryText.planAmount || '').trim();
    },
    planDate() {
      return resolvePayPlanDate(this.row);
    },
    planCustomer() {
      return resolvePayPlanCustomer(this.row, this.text.payModal.fallback.emptyValue);
    },
    planVoucherTypeLabel() {
      return resolvePayPlanVoucherTypeLabel(this.row, this.text);
    },
    planInvoicePaymentMethod() {
      return String(
        this.row?.invoice_billing_data?.payment_method?.label
        || this.row?.invoice_payment_method_label
        || this.row?.invoice_billing_data?.payment_method?.code
        || this.row?.invoice_payment_method
        || ''
      ).trim() || this.text.payModal.fallback.emptyValue;
    },
    remainingAmount() {
      return resolveRemainingAmount(this.form.amount, this.planAmount);
    },
    amountErrorMessage() {
      if (this.isExpenseFeeContext) {
        return resolveExpenseFeeAmountErrorMessage({
          amount: this.form.amount,
          planAmount: this.planAmount,
          entityType: this.expenseFeeEntityType,
          text: this.text,
        });
      }

      return resolveAmountErrorMessage(this.form.amount, this.planAmount, this.text);
    },
    amountWarningMessage() {
      if (this.isExpenseFeeContext) {
        return resolveExpenseFeeAmountWarningMessage({
          amount: this.form.amount,
          planAmount: this.planAmount,
          entityType: this.expenseFeeEntityType,
          text: this.text,
        });
      }

      return resolveAmountWarningMessage(this.form.amount, this.planAmount, this.text);
    },
    paymentDateModel: {
      get() {
        return parsePresentationDate(this.form.paymentDate);
      },
      set(value) {
        this.form.paymentDate = formatDateForApi(value);
      },
    },
    selectedContributorId() {
      return Number(this.selectedReceiver?.meta?.id || this.selectedReceiver?.value || this.selectedReceiver?.id || 0) || 0;
    },
    selectedCustomerId() {
      const selectedReceiverCustomerId = Number(
        this.selectedReceiver?.meta?.customerId
        || this.selectedReceiver?.customerId
        || this.selectedReceiver?.meta?.companyId
        || this.selectedReceiver?.companyId
        || 0
      ) || 0;

      if (selectedReceiverCustomerId > 0) {
        return selectedReceiverCustomerId;
      }

      return Number(this.row?.customer_id || this.row?.company_id || 0) || 0;
    },
    effectiveTaxRegimeOptions() {
      return Array.isArray(this.taxRegimeOptions) ? this.taxRegimeOptions : [];
    },
    effectiveCfdiUseOptions() {
      return Array.isArray(this.cfdiUseOptions) ? this.cfdiUseOptions : [];
    },
    effectivePaymentMethodOptions() {
      return Array.isArray(this.paymentMethodTypeOptions) ? this.paymentMethodTypeOptions : [];
    },
    effectivePaymentFormOptions() {
      return Array.isArray(this.paymentMethodOptions) ? this.paymentMethodOptions : [];
    },
    submitDisabled() {
      if (this.isExpenseFeeContext) {
        return this.submitting
          || this.loadingCatalogs
          || Number(this.form.amount || 0) <= 0
          || !!this.amountErrorMessage
          || String(this.form.paymentDate || '').trim() === ''
          || !this.form.paymentAccount;
      }

      const baseDisabled = this.submitting
        || this.loadingCatalogs
        || Number(this.form.amount || 0) <= 0
        || !!this.amountErrorMessage
        || String(this.form.paymentDate || '').trim() === ''
        || !this.form.paymentMethod;

      if (!this.isCfdiMode) {
        return baseDisabled;
      }

      return baseDisabled || this.loadingReceivers || !this.selectedReceiver;
    },
  },
  watch: {
    show: {
      immediate: true,
      handler(visible) {
        if (visible) {
          this.handleOpen();
          return;
        }

        this.resetForm();
      },
    },
    row: {
      deep: true,
      handler() {
        if (!this.show) {
          return;
        }

        this.hydrateFormFromRow();
        if (this.isCfdiMode) {
          this.resolveInitialReceiverSelection();
          return;
        }

        this.selectedReceiver = null;
      },
    },
  },
  methods: {
    resolveInvoicePaymentMethodData() {
      const paymentMethod = this.row?.invoice_billing_data?.payment_method;

      return {
        code: String(
          paymentMethod?.code
          || this.row?.invoice_payment_method
          || this.row?.payment_method
          || ''
        ).trim().toUpperCase(),
        label: String(
          paymentMethod?.label
          || this.row?.invoice_payment_method_label
          || this.row?.payment_method_label
          || ''
        ).trim().toLowerCase(),
      };
    },
    resolvePaymentMethodOptionByInvoiceData() {
      const options = Array.isArray(this.paymentMethodOptions) ? this.paymentMethodOptions : [];
      if (options.length === 0) {
        return null;
      }

      const invoiceMethod = this.resolveInvoicePaymentMethodData();
      const invoiceMethodCode = String(invoiceMethod?.code || '').trim().toUpperCase();
      const invoiceMethodLabel = String(invoiceMethod?.label || '').trim().toLowerCase();

      if (invoiceMethodCode !== '') {
        const byCode = options.find((option) => {
          const optionCode = String(option?.meta?.code || option?.value || '').trim().toUpperCase();
          return optionCode !== '' && optionCode === invoiceMethodCode;
        });

        if (byCode) {
          return byCode;
        }
      }

      if (invoiceMethodLabel !== '') {
        const byLabel = options.find((option) => {
          const optionLabel = String(option?.label || '').trim().toLowerCase();
          return optionLabel === invoiceMethodLabel || optionLabel.includes(invoiceMethodLabel);
        });

        if (byLabel) {
          return byLabel;
        }
      }

      return null;
    },
    prefillPaymentMethod() {
      const invoiceMethodOption = this.resolvePaymentMethodOptionByInvoiceData();
      const defaultTransfer = resolveDefaultTransferPaymentMethod(this.paymentMethodOptions);

      this.form.paymentMethod = invoiceMethodOption || defaultTransfer || null;
    },
    formatDateShortMonth(value) {
      return formatPayPlanDateShortMonth(value, this.text.payModal.fallback.emptyValue);
    },
    customerSearchLabel(option) {
      return resolvePayModalCustomerSearchLabel(option);
    },
    prefillBankData(receiver) {
      const bankData = extractPayModalBankData(receiver);

      this.form.beneficiaryBankRfc = bankData.beneficiaryBankRfc;
      this.form.beneficiaryAccountNumber = bankData.beneficiaryAccountNumber;
      this.form.orderingBank = bankData.orderingBank;
      this.form.orderingBankRfc = bankData.orderingBankRfc;
      this.form.orderingBankAccount = bankData.orderingBankAccount;
      this.bankDetailsExpanded = bankData.expanded;
    },
    resetForm() {
      this.form = {
        amount: 0,
        paymentDate: formatDateForApi(new Date()),
        paymentMethod: null,
        paymentAccount: null,
        operationNumber: '',
        beneficiaryBankRfc: '',
        beneficiaryAccountNumber: '',
        orderingBank: '',
        orderingBankRfc: '',
        orderingBankAccount: '',
      };
      this.currencyCode = 'MXN';
      this.exchangeRate = 1;
      this.bankDetailsExpanded = false;
      this.selectedReceiver = null;
      this.receiverOptions = [];
      this.taxRegimeOptions = [];
      this.cfdiUseOptions = [];
      this.paymentMethodTypeOptions = [];
      this.showContributorModal = false;
      this.contributorModalLoading = false;
      this.contributorModalMode = 'edit';
      this.contributorModalInitialData = null;
    },
    hydrateFormFromRow() {
      this.form = {
        ...this.form,
        amount: this.planAmount,
        paymentDate: formatDateForApi(new Date()),
      };
      this.currencyCode = String(this.row?.currency || 'MXN').trim().toUpperCase() || 'MXN';
      this.exchangeRate = Number(this.row?.exchange_rate || 1) || 1;
    },
    async handleOpen() {
      this.resetForm();
      this.hydrateFormFromRow();

      if (this.isExpenseFeeContext) {
        await this.loadCatalogs(false);
        return;
      }

      if (!this.isCfdiMode) {
        await this.loadCatalogs(false);
        return;
      }

      await Promise.all([
        this.loadCatalogs(true),
        this.loadReceiverOptions(),
      ]);

      await this.resolveInitialReceiverSelection();
      this.prefillBankData(this.selectedReceiver);
    },
    async loadCatalogs(loadCfdiCatalogs = false) {
      this.loadingCatalogs = true;

      try {
        const requests = [
          generalCatalogsService.getFormasPago(),
          financialAccountRepository.selectors(),
        ];

        if (loadCfdiCatalogs) {
          requests.push(generalCatalogsService.getRegimens());
          requests.push(generalCatalogsService.getUsosCfdi());
          requests.push(generalCatalogsService.getMetodosPago());
        }

        const results = await Promise.allSettled(requests);
        const paymentMethodsResult = results[0];
        const financialAccountsResult = results[1];
        const regimensResult = results[2];
        const usosCfdiResult = results[3];
        const paymentMethodTypesResult = results[4];

        if (paymentMethodsResult?.status === 'fulfilled') {
          this.paymentMethodOptions = normalizeCatalogOptions(extractCollection(paymentMethodsResult.value));
          this.prefillPaymentMethod();
        } else {
          this.paymentMethodOptions = [];
          this.form.paymentMethod = null;
        }

        if (financialAccountsResult?.status === 'fulfilled') {
          this.paymentAccountOptions = buildPaymentAccountOptions(
            extractCollection(financialAccountsResult.value),
            this.text.payModal.banking.unassignedAccount
          );
          this.form.paymentAccount = resolveDefaultPaymentAccount(this.paymentAccountOptions);
        } else {
          this.paymentAccountOptions = [];
          this.form.paymentAccount = null;
        }

        if (!loadCfdiCatalogs) {
          this.taxRegimeOptions = [];
          this.cfdiUseOptions = [];
          this.paymentMethodTypeOptions = [];
          return;
        }

        this.taxRegimeOptions = regimensResult?.status === 'fulfilled'
          ? normalizeCatalogOptions(extractCollection(regimensResult.value))
          : [];

        this.cfdiUseOptions = usosCfdiResult?.status === 'fulfilled'
          ? normalizeCatalogOptions(extractCollection(usosCfdiResult.value))
          : [];

        this.paymentMethodTypeOptions = paymentMethodTypesResult?.status === 'fulfilled'
          ? normalizeCatalogOptions(extractCollection(paymentMethodTypesResult.value))
          : [];
      } finally {
        this.loadingCatalogs = false;
      }
    },
    async loadReceiverOptions() {
      if (!this.isCfdiMode) {
        this.receiverOptions = [];
        return;
      }

      this.loadingReceivers = true;

      try {
        const customerId = this.invoiceCustomerId;
        const response = await invoicesService.receiverSelectors(
          customerId > 0 ? { customer_id: customerId } : {}
        );
        const rows = extractCollection(response);
        const mappedOptions = rows
          .map((item) => mapPayModalReceiverSelectorOption(item))
          .filter((option) => Number(option?.value || 0) > 0);

        this.receiverOptions = customerId > 0
          ? mappedOptions.filter((option) => Number(option?.customerId || option?.meta?.customerId || 0) === customerId)
          : mappedOptions;
      } catch (error) {
        this.receiverOptions = [];
      } finally {
        this.loadingReceivers = false;
      }
    },
    async resolveInitialReceiverSelection() {
      if (!this.isCfdiMode) {
        this.selectedReceiver = null;
        return;
      }

      const contributorId = Number(
        this.row?.contributor_id
        || this.row?.invoice_contributor_id
        || this.row?.invoice_billing_data?.contributor?.id
        || 0
      ) || 0;
      const customerId = this.invoiceCustomerId;
      const billedRfc = String(
        this.row?.invoice_billing_data?.contributor?.rfc
        || this.row?.invoice_billing_data?.rfc
        || this.row?.invoice_billed_rfc
        || ''
      ).trim().toUpperCase();

      const optionByRfc = resolveReceiverOptionByRfc(this.receiverOptions, billedRfc);
      if (optionByRfc) {
        this.selectedReceiver = optionByRfc;
        return;
      }

      const optionByContributorId = resolveReceiverOptionByContributorId(this.receiverOptions, contributorId);
      if (optionByContributorId) {
        this.selectedReceiver = optionByContributorId;
        return;
      }

      if (contributorId > 0) {
        try {
          const response = await invoicesService.contributorShow(contributorId);
          const payload = extractPayload(response);
          if (payload && typeof payload === 'object') {
            const mapped = mapPayModalContributorDetailOption(payload);
            const mappedCustomerId = Number(mapped?.customerId || mapped?.meta?.customerId || 0) || 0;
            const isAllowedCustomer = customerId <= 0 || mappedCustomerId <= 0 || mappedCustomerId === customerId;
            if (Number(mapped?.value || 0) > 0 && isAllowedCustomer) {
              this.selectedReceiver = mapped;
              return;
            }
          }
        } catch (error) {
          // no-op
        }
      }

      this.selectedReceiver = resolveDefaultReceiverOption(this.receiverOptions);
    },
    async openContributorModal() {
      if (this.selectedContributorId <= 0) {
        this.$toast?.error(this.text.payModal.billingData.validation.receiverRequiredToEdit);
        return;
      }

      this.contributorModalLoading = true;
      try {
        const response = await invoicesService.contributorShow(this.selectedContributorId);
        const payload = extractPayload(response);

        this.contributorModalInitialData = payload && typeof payload === 'object' ? { ...payload } : null;
        this.contributorModalMode = 'edit';
        this.showContributorModal = true;
      } catch (error) {
        const message = error?.response?.data?.message
          || this.text.payModal.billingData.toasts.loadContributorError;
        this.$toast?.error(message);
      } finally {
        this.contributorModalLoading = false;
      }
    },
    closeContributorModal() {
      this.showContributorModal = false;
      this.contributorModalLoading = false;
      this.contributorModalMode = 'edit';
      this.contributorModalInitialData = null;
    },
    async onContributorModalSubmit(payload) {
      if (this.selectedCustomerId <= 0 || this.selectedContributorId <= 0) {
        this.$toast?.error(this.text.payModal.billingData.validation.receiverRequiredToEdit);
        return;
      }

      this.contributorModalLoading = true;
      try {
        await customersService.contributorsUpdate(this.selectedCustomerId, this.selectedContributorId, payload || {});

        await this.loadReceiverOptions();
        const updatedReceiver = this.receiverOptions.find((option) => Number(option?.value || 0) === this.selectedContributorId) || null;
        if (updatedReceiver) {
          this.selectedReceiver = updatedReceiver;
        }

        this.$toast?.success(this.text.payModal.billingData.toasts.updateContributorSuccess);
        this.closeContributorModal();
      } catch (error) {
        const message = error?.response?.data?.message
          || this.text.payModal.billingData.toasts.updateContributorError;
        this.$toast?.error(message);
        this.contributorModalLoading = false;
      }
    },
    emitClose() {
      if (this.submitting) {
        return;
      }

      this.$emit('close');
    },
    buildSubmitPayload(options = {}) {
      const shouldStampPayment = this.isCfdiMode && Number(options?.shouldStampPayment || 0) === 1;

      const paymentMethodCode = String(
        this.form.paymentMethod?.meta?.code
        || this.form.paymentMethod?.value
        || ''
      ).trim();

      const rawPaymentMethodId = Number(this.form.paymentMethod?.value);

      const payload = {
        payment_plan_id: this.isExpenseFeeContext ? 0 : (Number(this.row?.id || 0) || 0),
        amount: Number(this.form.amount || 0) || 0,
        payment_date: String(this.form.paymentDate || '').trim(),
        currency: this.currencyCode,
        exchange_rate: Number(this.exchangeRate || 1) || 1,
        payment_form: paymentMethodCode || '03',
        payment_method_id: Number.isFinite(rawPaymentMethodId) ? rawPaymentMethodId : 0,
        payment_account_id: Number(this.form.paymentAccount?.value || 0) || 0,
        operation_number: String(this.form.operationNumber || '').trim(),
        should_stamp_payment: shouldStampPayment ? 1 : 0,
        stamp_payment: shouldStampPayment ? 1 : 0,
        timbrar: shouldStampPayment ? 1 : 0,
      };

      if (this.isExpenseFeeContext) {
        const rowId = Number(this.row?.id || 0) || 0;
        const entityType = String(this.entityType || '').trim().toLowerCase();

        return {
          ...payload,
          payment_plan_id: 0,
          expense_id: entityType === 'expense' ? rowId : 0,
          fee_id: entityType === 'fee' ? rowId : 0,
          context: 'expense-fee',
        };
      }

      if (!this.isCfdiMode) {
        return payload;
      }

      return {
        ...payload,
        beneficiary_bank_rfc: String(this.form.beneficiaryBankRfc || '').trim(),
        beneficiary_account_number: String(this.form.beneficiaryAccountNumber || '').trim(),
        ordering_bank: String(this.form.orderingBank || '').trim(),
        ordering_bank_rfc: String(this.form.orderingBankRfc || '').trim(),
        ordering_bank_account_number: String(this.form.orderingBankAccount || '').trim(),
        contributor_id: this.selectedContributorId,
      };
    },
    emitSubmit(options = {}) {
      if (this.submitDisabled) {
        return;
      }

      this.$emit('submit', this.buildSubmitPayload(options));
    },
    emitSubmitCfdi() {
      this.emitSubmit({ shouldStampPayment: 1 });
    },
  },
};
</script>
